import InputErrorMessage from "@/components/InputErrorMessage"
import { TextInput } from "@/components/InputFields"
import cn from "@/utils/cn.ts"
import {
  generateTimesOfDay,
  isValidTime,
  parseAndFormatTime,
} from "@/utils/time-utils"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { Duration } from "@js-joda/core"
import { ChangeEvent, useMemo, useState } from "react"

type TimePickerProps = {
  value: string
  onChange: (newTime: string) => void
  errorMessage?: string
}

const TimePicker = ({ value, onChange, errorMessage }: TimePickerProps) => {
  const [isTimeValid, setIsTimeValid] = useState(true)
  const [parsedTime, setParsedTime] = useState(value)

  const timesList = useMemo(
    () => generateTimesOfDay({ step: Duration.ofMinutes(15) }),
    []
  )

  const scrollToSelectedTime = () => {
    const item = document.querySelector("#selected")
    if (item) item.scrollIntoView({ behavior: "auto", block: "start" })
  }

  const handleBlur = () => {
    if (!isValidTime(parsedTime)) {
      setIsTimeValid(false)
      return
    }
    if (parsedTime) {
      onChange(parsedTime)
      setIsTimeValid(true)
    } else setIsTimeValid(false)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const parsed = parseAndFormatTime(e.target.value)
    if (parsed) {
      setParsedTime(parsed)
      setIsTimeValid(isValidTime(parsed))
    } else {
      setParsedTime("")
    }
    onChange(e.target.value)
  }

  return (
    <div className="relative w-full">
      <Popover className="relative">
        <PopoverButton className="cursor-pointer absolute inset-y-0 right-0 flex items-center pr-3">
          <span className="material-icons-outlined cursor-pointer text-icon-lg">
            access_time
          </span>
        </PopoverButton>
        <PopoverPanel
          transition
          anchor="bottom end"
          className="z-9999 flex h-[260px] w-[200px] flex-col divide-y rounded-lg border border-primary/20 bg-white"
        >
          {({ close, open }) => {
            if (open) scrollToSelectedTime()
            return (
              <ul className="py-2">
                {timesList.map((time, index) => (
                  <li
                    id={open && time === value ? "selected" : ""}
                    key={index}
                    value={time}
                    className={cn(
                      "cursor-pointer px-4 py-1.5 text-sm transition hover:bg-gray-200",
                      time === value && "bg-gray-200"
                    )}
                    onClick={() => {
                      onChange(time)
                      setParsedTime(time)
                      setIsTimeValid(true)
                      close()
                    }}
                  >
                    {time}
                  </li>
                ))}
              </ul>
            )
          }}
        </PopoverPanel>
        <TextInput
          placeholder="hh:mm"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          isError={!isTimeValid || !!errorMessage}
        />
      </Popover>
      <InputErrorMessage
        message={!isTimeValid ? "Invalid time" : errorMessage}
        className="mt-1"
      />
    </div>
  )
}

export default TimePicker
